import { memo, useCallback, useState } from 'react';
import { Dialog } from 'react-aria-components';

import { merge } from '@/components';
import { Modal, ModalOverlay, ModalOverlayProps } from '@/components/core';
import { Button, Flex, Heading, Text } from '@/components/core';
import { CloseIcon, SaveIcon } from '@/components/icons';
import { DivProps } from '@/utils';

type DeleteProps = Omit<DivProps, 'children'> & {
  heading?: string;
  description?: string;
  onClose?: () => void;
  onDelete?: () => void;
};

const IDelete = ({ heading, description, onDelete, onClose, className, ...rest }: DeleteProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const onDeleteHandler = useCallback(() => {
    setIsDeleting(true);
    onDelete?.();
    setIsDeleting(false);
    onClose?.();
  }, [onClose, onDelete]);

  return (
    <Flex col className={merge('gap-md', className)} {...rest}>
      <Flex className="items-start">
        <Flex col className="gap-xs">
          <Heading>{heading}</Heading>
          <Text>{description}</Text>
        </Flex>
        <div className="grow" />
        <Button onPress={onClose} variant="plain">
          <CloseIcon />
        </Button>
      </Flex>
      <Flex className="gap-sm justify-end">
        <Button onPress={onClose} variant="secondary" isDisabled={isDeleting}>
          <Text>Cancel</Text>
        </Button>
        <Button onPress={onDeleteHandler} variant="danger" isDisabled={isDeleting}>
          {isDeleting ? <SaveIcon /> : null}
          <Text>Delete</Text>
        </Button>
      </Flex>
    </Flex>
  );
};

const Delete = memo(IDelete) as typeof IDelete;

export type DeleteModalProps = ModalOverlayProps & {
  heading?: string;
  description?: string;
  onDelete?: () => void;
  onClose?: () => void;
};

const IDeleteModal = ({ heading, description, onDelete, onClose, ...rest }: DeleteModalProps) => {
  return (
    <ModalOverlay {...rest}>
      <Modal>
        <Dialog className="outline-none">
          {({ close }) => (
            <Delete
              className="w-[25rem] p-4"
              heading={heading}
              description={description}
              onDelete={onDelete}
              onClose={() => {
                onClose?.();
                close();
              }}
            />
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

export const DeleteModal = memo(IDeleteModal) as typeof IDeleteModal;
